import type { Logger } from "@expert/logging";
import { trackAppcuesEvent } from "@expert/monitoring";
import type { Partner } from "@expert/shared-types";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { getFeature } from "@soluto-private/expert-workspace-feature-flagging";
import { useEffect, useMemo, useState } from "react";
import type { Group } from "./types";

export interface ContextProps {
    agentSite?: string;
    appName: "expert-assist-standalone" | "expert-workspace";
    employeeId: string;
    environment: string;
    lob?: string;
    partner: Partner;
    source: "expert-assist" | "expert-ui";
}

export interface FeatureFlagProps<T extends Record<string, unknown>> {
    analyticGroup: Group;
    context: ContextProps;
    features: T;
    logger: Logger;
}

export function useFlags<T extends Record<string, unknown>>({
    analyticGroup: group,
    context,
    features,
    logger: loggerProp,
}: FeatureFlagProps<T>) {
    const [updatedFeatures, setUpdatedFeatures] = useState(features);
    const logger = useMemo(() => loggerProp.child({ module: "useFlags" }), [loggerProp]);

    const { dispatcher } = useReactAnalytics();

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        const { employeeId, partner } = context;

        if (!employeeId || !partner) {
            logger.info({ context }, "Feature Flags | missing context for fetching flags");
            return;
        }

        const fetchFeatures = async () => {
            const featureKeys = Object.keys(features);

            logger.info({ context, featureKeys }, "Feature Flags | fetching flags");

            const promises = featureKeys.map(async (feature) => {
                const data = await getFeature(feature, { ...context });
                return { [feature]: data };
            });

            try {
                const results = await Promise.all(promises);

                let resultsObject = { ...features };

                results.forEach((result) => {
                    // don't overwrite features if data value comes back undefined from barvidently
                    if (Object.values(result)[0] !== undefined) {
                        resultsObject = { ...resultsObject, ...result };
                    }
                });

                setUpdatedFeatures(resultsObject);

                logger.info({ context, features: resultsObject }, "Feature Flags | fetched flags");

                void dispatcher.withExtra({ partner }).dispatchBusinessEvent("FeatureFlagResults", {
                    ...context,
                    features: resultsObject,
                    group,
                });
                trackAppcuesEvent("FeatureFlagResults", { ...context, features: resultsObject, group });
            } catch (error: unknown) {
                setUpdatedFeatures({ ...features });
                logger.error({ context, error }, "Feature Flags | error fetching flags");
            }
        };

        void fetchFeatures();
    }, [context, features]);

    return updatedFeatures;
}
